export interface ActionField {
  name: string;
  label: string;
  type:
    | 'string'
    | 'number'
    | 'array'
    | 'select'
    | 'textarea'
    | 'fields-list'
    | 'potential-actions-list'
    | 'schema-field-completion'
    | 'async-select'
    | 'comment';
  placeholder?: string;
  isRequired?: boolean;
  isConfigurable?: boolean;
  defaultValue?: any;
  minValue?: number;
  maxValue?: number;
  integerOnly?: boolean;
  options?: { title: string; value: string }[];
  dataType?: 'zia-url-category' | 'user' | 'status' | 'verdict' | 'okta-group';
}

export interface ActionDefinition {
  type: string; // Internal ActionType enum name, matching what the backend stores in action_catalog
  label: string;
  fields: ActionField[];
}

const severityOptions = [
  { title: 'None', value: 'NONE' },
  { title: 'Low', value: 'LOW' },
  { title: 'Medium', value: 'MEDIUM' },
  { title: 'High', value: 'HIGH' },
  { title: 'Critical', value: 'CRITICAL' },
];

export const SUPPORTED_ACTIONS: ActionDefinition[] = [
  {
    type: 'RC_SEND_SMS_MESSAGE',
    label: 'Send SMS',
    fields: [
      {
        name: 'phone_numbers',
        label: 'Phone Numbers',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Comma-separated phone numbers',
      },
      {
        name: 'message',
        label: 'Message',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'SMS content',
      },
    ],
  },
  {
    type: 'SEND_EMAIL',
    label: 'Send Email',
    fields: [
      { name: 'to', label: 'To', type: 'string', isRequired: true, isConfigurable: true, placeholder: 'recipient@example.com' },
      {
        name: 'subject',
        label: 'Subject',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Security Alert',
      },
      {
        name: 'body',
        label: 'Body',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Email body',
      },
      { name: 'cc', label: 'CC', type: 'string', placeholder: 'manager@example.com' },
      { name: 'bcc', label: 'BCC', type: 'string', placeholder: 'audit@example.com' },
      { name: 'reply_to', label: 'Reply To', type: 'string', placeholder: 'soc@example.com' },
    ],
  },
  {
    type: 'RC_CALL_PHONE_NUMBERS',
    label: 'Call Phone Number',
    fields: [
      {
        name: 'phone_numbers',
        label: 'Phone Numbers',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Comma-separated phone numbers',
      },
      {
        name: 'message',
        label: 'Message',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Voice message content',
      },
      { name: 'seconds_between_calls', label: 'Seconds Between Calls', type: 'number', placeholder: '0' },
    ],
  },
  {
    type: 'INVOKE_WEBHOOK',
    label: 'Send Webhook',
    fields: [
      {
        name: 'url',
        label: 'URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'https://hooks.example.com/incident',
      },
      {
        name: 'httpMethod',
        label: 'HTTP Method',
        type: 'select',
        isRequired: true,
        isConfigurable: true,
        options: [
          { title: 'GET', value: 'get' },
          { title: 'POST', value: 'post' },
          { title: 'PUT', value: 'put' },
          { title: 'PATCH', value: 'patch' },
        ],
      },
      {
        name: 'payloadValue',
        label: 'Payload',
        type: 'schema-field-completion',
        placeholder: 'Webhook payload',
        isConfigurable: true,
      },
      {
        name: 'allowUntrusted',
        label: 'Allow Untrusted',
        type: 'select',
        options: [
          { title: 'True', value: 'true' },
          { title: 'False', value: 'false' },
        ],
      },
      { name: 'httpHeaders', label: 'HTTP Headers (JSON)', type: 'textarea', placeholder: '{"Header": "Value"}' },
    ],
  },
  {
    type: 'SLACK_SEND_WEBHOOK',
    label: 'Send Slack Message',
    fields: [
      {
        name: 'url',
        label: 'Webhook URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'https://hooks.slack.com/services/...',
      },
      { name: 'channel', label: 'Channel', type: 'string', placeholder: '#security-alerts', isConfigurable: true },
      {
        name: 'title',
        label: 'Message Title',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Critical alert from CrowdStrike',
      },
      { name: 'title_link', label: 'Title Link', type: 'string', placeholder: 'https://portal.example.com' },
      { name: 'text', label: 'Message Text', type: 'schema-field-completion', placeholder: 'Message text content', isConfigurable: true },
      { name: 'color', label: 'Color', type: 'string', placeholder: 'good, warning, danger, or hex code' },
    ],
  },
  {
    type: 'RC_MS_TEAM_SEND_WEBHOOK',
    label: 'Send MS Team Message',
    fields: [
      {
        name: 'url',
        label: 'Webhook URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'https://outlook.office.com/webhook/...',
      },
      {
        name: 'title',
        label: 'Message Title',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Security Alert',
      },
      {
        name: 'text',
        label: 'Message Text',
        type: 'schema-field-completion',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Suspicious activity detected.',
      },
    ],
  },
  {
    type: 'RC_SEND_SYSLOG',
    label: 'Send Syslog',
    fields: [
      {
        name: 'serverURL',
        label: 'Server URL',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'syslog.example.com',
      },
      {
        name: 'serverPort',
        label: 'Server Port',
        type: 'number',
        isRequired: true,
        isConfigurable: true,
        minValue: 1,
        maxValue: 65535,
        integerOnly: true,
        placeholder: '514',
      },
      {
        name: 'protocol',
        label: 'Protocol',
        type: 'select',
        isRequired: true,
        isConfigurable: true,
        options: [
          { title: 'TCP', value: 'tcp' },
          { title: 'TCP/SSL', value: 'tcp_ssl' },
          { title: 'UDP', value: 'udp' },
        ],
      },
      { name: 'program', label: 'Program', type: 'string', isRequired: true, isConfigurable: true, placeholder: 'red_canary' },
      { name: 'message', label: 'Message', type: 'schema-field-completion', isRequired: true, isConfigurable: true },
      {
        name: 'allowUntrusted',
        label: 'Allow Untrusted',
        type: 'select',
        options: [
          { title: 'True', value: 'true' },
          { title: 'False', value: 'false' },
        ],
      },
      { name: 'facility', label: 'Facility', type: 'string' },
      { name: 'severity', label: 'Severity', type: 'string' },
    ],
  },
  {
    type: 'RC_CS_ISOLATE_ENDPOINT',
    label: 'CrowdStrike Isolate Endpoint',
    fields: [
      { name: 'aid', label: 'Agent ID (AID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: '32-character hex ID' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'CrowdStrike credentials namespace',
      },
    ],
  },
  {
    type: 'RC_CS_DEISOLATE_ENDPOINT',
    label: 'CrowdStrike Deisolate Endpoint',
    fields: [
      { name: 'aid', label: 'Agent ID (AID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: '32-character hex ID' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'CrowdStrike credentials namespace',
      },
    ],
  },
  {
    type: 'RC_OKTA_SUSPEND_USER',
    label: 'Suspend Okta User',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'RC_OKTA_UNSUSPEND_USER',
    label: 'Unsuspend Okta User',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'RC_OKTA_CLEAR_USER_SESSIONS',
    label: 'Clear Okta User Sessions',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'RC_OKTA_ADD_USER_TO_GROUP',
    label: 'Add Okta User to Group',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'okta_group_id',
        label: 'Okta Group',
        type: 'async-select',
        dataType: 'okta-group',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Select Okta group',
      },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'RC_OKTA_REMOVE_USER_FROM_GROUP',
    label: 'Remove Okta User from Group',
    fields: [
      { name: 'okta_user_id', label: 'Okta User ID', type: 'string', isRequired: true, isConfigurable: false, placeholder: '00u...' },
      {
        name: 'okta_group_id',
        label: 'Okta Group',
        type: 'async-select',
        dataType: 'okta-group',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Select Okta group',
      },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Okta credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ENTRA_ID_FORCE_PASSWORD_RESET',
    label: 'Entra ID Force Password Reset',
    fields: [
      { name: 'user_id', label: 'User ID (UUID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: 'a1b2c3d4-...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Entra ID credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ENTRA_ID_RISKY_USER_CONFIRM_COMPROMISED',
    label: 'Entra ID Confirm Compromised',
    fields: [
      { name: 'user_id', label: 'User ID (UUID)', type: 'string', isRequired: true, isConfigurable: false, placeholder: 'a1b2c3d4-...' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Entra ID credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ZIA_ADD_HASH_TO_ALLOW_LIST',
    label: 'Zscaler Add Hash to Allow List',
    fields: [
      { name: 'hashes', label: 'Hashes', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of file hashes' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ZIA_REMOVE_HASH_FROM_ALLOW_LIST',
    label: 'Zscaler Remove Hash from Allow List',
    fields: [
      { name: 'hashes', label: 'Hashes', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of file hashes' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ZIA_ADD_IP_TO_SRC_IP_GROUP',
    label: 'Zscaler Add IP to Source Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler source IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ZIA_REMOVE_IP_TO_SRC_IP_GROUP',
    label: 'Zscaler Remove IP from Source Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler source IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ZIA_ADD_IP_TO_DEST_IP_GROUP',
    label: 'Zscaler Add IP to Destination Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler destination IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ZIA_REMOVE_IP_FROM_DEST_IP_GROUP',
    label: 'Zscaler Remove IP from Destination Group',
    fields: [
      {
        name: 'group_id',
        label: 'Group ID',
        type: 'string',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Zscaler destination IP group ID',
      },
      { name: 'ips', label: 'IPs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of IP addresses' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
    ],
  },
  {
    type: 'RC_ZIA_ADD_TO_URL_CATEGORY',
    label: 'Zscaler Add URL to Category',
    fields: [
      {
        name: 'category_id',
        label: 'Category',
        type: 'async-select',
        dataType: 'zia-url-category',
        isRequired: true,
        isConfigurable: true,
      },
      { name: 'urls', label: 'URLs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of URLs' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
      { name: 'configured_name', label: 'Friendly Name', type: 'string', placeholder: 'Optional friendly name' },
    ],
  },
  {
    type: 'RC_ZIA_REMOVE_FROM_URL_CATEGORY',
    label: 'Zscaler Remove URL from Category',
    fields: [
      {
        name: 'category_id',
        label: 'Category',
        type: 'async-select',
        dataType: 'zia-url-category',
        isRequired: true,
        isConfigurable: true,
      },
      { name: 'urls', label: 'URLs', type: 'array', isRequired: true, isConfigurable: false, placeholder: 'Array of URLs' },
      {
        name: 'external_service_namespace',
        label: 'Service Namespace',
        type: 'string',
        isRequired: true,
        isConfigurable: false,
        placeholder: 'Zscaler credentials namespace',
      },
      { name: 'configured_name', label: 'Friendly Name', type: 'string', placeholder: 'Optional friendly name' },
    ],
  },
  {
    type: 'FABRIC_CHANGE_STATUS',
    label: 'Change Status',
    fields: [
      {
        name: 'status',
        label: 'Status',
        type: 'async-select',
        dataType: 'status',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Select status',
      },
    ],
  },
  {
    type: 'FABRIC_ASSIGN_OWNER',
    label: 'Assign Owner',
    fields: [
      {
        name: 'owner',
        label: 'Owner',
        type: 'async-select',
        dataType: 'user',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Analyst or SOC team name',
      },
    ],
  },
  {
    type: 'FABRIC_ADD_COMMENT',
    label: 'Add Comment',
    fields: [
      {
        name: 'comment',
        label: 'Comment',
        type: 'comment',
        isRequired: true,
        isConfigurable: true,
        placeholder: 'Notes or evidence',
      },
    ],
  },
  {
    type: 'FABRIC_SET_SEVERITY',
    label: 'Set Severity',
    fields: [
      {
        name: 'severity',
        label: 'Severity',
        type: 'select',
        isRequired: true,
        isConfigurable: true,
        options: severityOptions,
      },
    ],
  },
];
